import { Autocomplete } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { ResponseAbortable } from 'shell/hooks/use-http-fetch/stateless/type';
import { CirculationArea } from 'shell/models/circulation-area';
import { HttpResponseList } from 'shell/models/http-response';
import debounce from 'shell/utils/debounce';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import PmiTextField from 'shell/components/shared/text-field';
import { CirculationAreaClient } from 'shell/view-models/circulation-area-client';

const MinSearchLength = 3;

interface FormTownHallProps {
  selectedValue: CirculationAreaClient | null;
  handleChange: (value: CirculationAreaClient | null) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  error?: boolean;
  label?: string;
}

const AddressAutoComplete = ({
  selectedValue,
  handleChange,
  required,
  disabled = false,
  error,
  id = 'address',
  label = 'Indirizzo',
}: FormTownHallProps): React.ReactElement => {
  const [circulationAreas, getCirculationAreas] = useHttpFetch<
    HttpResponseList<CirculationArea>,
    Array<CirculationAreaClient>
  >(`${__TOPONYMY_API_URL__}/areaCircolazione`, {
    options: {
      method: 'GET',
      abortable: true,
      errorCatching: true,
      loading: false,
    },
    transformer: (response) => {
      return response.data.payload.list.map((circulationArea) => ({
        circulationArea,
        description: circulationArea.descrizionePostale || '',
        code: circulationArea.key?.codice,
      }));
    },
  });

  const defaultProps = useMemo(() => {
    return {
      options: (circulationAreas.data as Array<CirculationAreaClient>) || [],
      getOptionLabel: (option: CirculationAreaClient) => option.description || '',
    };
  }, [circulationAreas.data]);

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        if (circulationAreas.abort) {
          circulationAreas.abort();
        }

        getCirculationAreas({
          params: {},
          paramsSerializer: () => `descrizionePostale=${value}&sort=descrizionePostale:ASC`,
        }) as ResponseAbortable<HttpResponseList<CirculationArea>, Array<CirculationAreaClient>>;
      },
      [getCirculationAreas, circulationAreas.abort]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      value={selectedValue}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        handleChange(element);
      }}
      disabled={disabled}
      onInputChange={(event, value, reason) => {
        if (value.length >= MinSearchLength && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={circulationAreas.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          error={error}
          label={required ? `${label} *` : label}
          variant="outlined"
        />
      )}
    />
  );
};

AddressAutoComplete.displayName = 'Address Auto Complete component';

export default AddressAutoComplete;
